/* ==========================================================================
   ONYX mobile drawer — minimal vanilla JS, no dependencies, no frameworks.

   Two-level slide-panel navigation. Progressive enhancement: if this file
   never executes, the theme's own mobile menu keeps working, because the
   trigger is only intercepted once this script has bound to it.
   ========================================================================== */
(function () {
  'use strict';

  /* The verified Pipeline hamburger, matched exactly. A bare
     .header__mobile__button (or [aria-label="Open menu"]) can also match the
     cart, search or account controls in the same header row, which would let
     this drawer swallow their clicks. */
  var DEFAULT_TRIGGERS = [
    '[data-onyx-drawer-open]',
    '.header__mobile__button[aria-label="Open menu"]'
  ];

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function trim(s) { return s.trim(); }

  function OnyxDrawer(root) {
    this.root = root;
    this.panel = root.querySelector('.onyx-drawer');
    this.overlay = root.querySelector('.onyx-drawer__overlay');
    if (!this.panel) return;

    var attr = (root.getAttribute('data-onyx-triggers') || '').trim();
    this.triggerSelectors = attr ? attr.split(',').map(trim).filter(Boolean) : DEFAULT_TRIGGERS;

    this.panels = this.panel.querySelectorAll('[data-onyx-panel]');
    this.rootPanel = this.panel.querySelector('[data-onyx-panel="root"]');
    this.current = this.rootPanel;
    this.openerRow = null;

    this.searchPanel = this.panel.querySelector('[data-onyx-search-panel]');
    this.searchToggle = this.panel.querySelector('[data-onyx-search-toggle]');
    this.searchInput = this.panel.querySelector('[data-onyx-search-input]');
    this.searchOpen = false;

    this.isOpen = false;
    this.lastTrigger = null;
    this.scrollY = 0;

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.setViewportUnit = this.setViewportUnit.bind(this);

    document.addEventListener('click', this.onDocumentClick, true);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.setViewportUnit);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.setViewportUnit);
    }

    this.setViewportUnit();
    this.syncTriggerState(false);
  }

  /* ---------- Viewport height ----------
     Mobile browser chrome changes the viewport height mid-scroll. dvh handles
     it where supported; this keeps the fallback custom property in step. */
  OnyxDrawer.prototype.setViewportUnit = function () {
    var h = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
    this.root.style.setProperty('--onyx-d-vh', (h * 0.01) + 'px');
  };

  /* ---------- Trigger binding ---------- */

  OnyxDrawer.prototype.matchTrigger = function (target) {
    for (var i = 0; i < this.triggerSelectors.length; i++) {
      var hit = target.closest(this.triggerSelectors[i]);
      if (hit) return hit;
    }
    return null;
  };

  OnyxDrawer.prototype.syncTriggerState = function (expanded) {
    var self = this;
    this.triggerSelectors.forEach(function (selector) {
      var nodes;
      try { nodes = document.querySelectorAll(selector); } catch (e) { return; }
      Array.prototype.forEach.call(nodes, function (node) {
        node.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        node.setAttribute('aria-controls', self.panel.id);
      });
    });
  };

  OnyxDrawer.prototype.onDocumentClick = function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    if (target.closest('[data-onyx-search-toggle]')) {
      event.preventDefault();
      this.toggleSearch();
      return;
    }

    if (target.closest('[data-onyx-search-close]')) {
      event.preventDefault();
      this.closeSearch(true);
      return;
    }

    if (target.closest('[data-onyx-drawer-close]')) {
      event.preventDefault();
      this.close();
      return;
    }

    var toPanel = target.closest('[data-onyx-to]');
    if (toPanel && this.panel.contains(toPanel)) {
      event.preventDefault();
      this.goTo(toPanel.getAttribute('data-onyx-to'), toPanel);
      return;
    }

    if (target.closest('[data-onyx-back]') && this.panel.contains(target)) {
      event.preventDefault();
      this.goBack();
      return;
    }

    if (this.root.contains(target)) return;

    var trigger = this.matchTrigger(target);
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();
    this.lastTrigger = trigger;
    this.open();
  };

  /* ---------- Panel navigation ---------- */

  OnyxDrawer.prototype.setPanelState = function (panel, state) {
    panel.classList.toggle('is-current', state === 'current');
    panel.classList.toggle('is-behind', state === 'behind');

    if (state === 'current') {
      panel.removeAttribute('aria-hidden');
      panel.removeAttribute('inert');
    } else {
      panel.setAttribute('aria-hidden', 'true');
      panel.setAttribute('inert', '');
    }
  };

  OnyxDrawer.prototype.goTo = function (panelId, openerRow) {
    var next = this.panel.querySelector('[data-onyx-panel="' + panelId + '"]');
    if (!next || next === this.current) return;

    if (openerRow) {
      openerRow.setAttribute('aria-expanded', 'true');
      this.openerRow = openerRow;
    }

    this.setPanelState(this.current, 'behind');
    this.setPanelState(next, 'current');
    this.current = next;

    var back = next.querySelector('[data-onyx-back]');
    if (back) back.focus({ preventScroll: true });
  };

  OnyxDrawer.prototype.goBack = function () {
    if (!this.rootPanel || this.current === this.rootPanel) return;

    this.setPanelState(this.current, 'hidden');
    this.setPanelState(this.rootPanel, 'current');
    this.current = this.rootPanel;

    if (this.openerRow) {
      this.openerRow.setAttribute('aria-expanded', 'false');
      if (this.panel.contains(this.openerRow)) {
        this.openerRow.focus({ preventScroll: true });
      }
      this.openerRow = null;
    }
  };

  OnyxDrawer.prototype.resetPanels = function () {
    var self = this;
    Array.prototype.forEach.call(this.panels, function (p) {
      self.setPanelState(p, p === self.rootPanel ? 'current' : 'hidden');
    });
    Array.prototype.forEach.call(
      this.panel.querySelectorAll('[data-onyx-to]'),
      function (row) { row.setAttribute('aria-expanded', 'false'); }
    );
    this.current = this.rootPanel;
    this.openerRow = null;
  };

  /* ---------- Collapsible search ----------
     Height is measured from the panel's own content rather than hard-coded, so
     the transition stays correct if the field or its padding change. */

  OnyxDrawer.prototype.toggleSearch = function () {
    if (this.searchOpen) {
      this.closeSearch(true);
    } else {
      this.openSearch();
    }
  };

  OnyxDrawer.prototype.openSearch = function () {
    if (!this.searchPanel || this.searchOpen) return;
    this.searchOpen = true;

    var panel = this.searchPanel;
    panel.hidden = false;

    var inner = panel.firstElementChild;
    var h = inner ? inner.offsetHeight : 0;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      panel.style.height = 'auto';
    } else {
      panel.style.height = '0px';
      void panel.offsetHeight;
      panel.style.height = h + 'px';

      // Settle to auto once the reveal is over. This is also the guarantee that
      // the panel ends at full height even if the transition never runs (a
      // throttled or backgrounded frame), and it lets the panel reflow if the
      // font swaps or a long value wraps.
      var settled = false;
      var settle = function () {
        if (settled || !this.searchOpen) { return; }
        settled = true;
        panel.removeEventListener('transitionend', settle);
        if (this.searchOpen) panel.style.height = 'auto';
      }.bind(this);
      panel.addEventListener('transitionend', settle);
      setTimeout(settle, 320);
    }

    if (this.searchToggle) this.searchToggle.setAttribute('aria-expanded', 'true');
    if (this.searchInput) this.searchInput.focus({ preventScroll: true });
  };

  OnyxDrawer.prototype.closeSearch = function (returnFocus) {
    if (!this.searchPanel || !this.searchOpen) return;
    this.searchOpen = false;

    var panel = this.searchPanel;
    var self = this;

    if (this.searchToggle) this.searchToggle.setAttribute('aria-expanded', 'false');

    var done = function () {
      panel.hidden = true;
      panel.style.height = '';
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      done();
    } else {
      panel.style.height = panel.offsetHeight + 'px';
      void panel.offsetHeight;
      panel.style.height = '0px';

      var fired = false;
      var handler = function () {
        if (fired) return;
        fired = true;
        panel.removeEventListener('transitionend', handler);
        done();
      };
      panel.addEventListener('transitionend', handler);
      // The timeout is the guarantee: transitionend does not fire in a
      // throttled frame, and the panel must never be left mid-collapse.
      setTimeout(handler, 320);
    }

    if (returnFocus && this.searchToggle && document.body.contains(this.searchToggle)) {
      this.searchToggle.focus({ preventScroll: true });
    }
  };

  /* ---------- Open / close ---------- */

  OnyxDrawer.prototype.open = function () {
    if (this.isOpen) return;
    this.isOpen = true;

    this.setViewportUnit();
    this.panel.hidden = false;
    if (this.overlay) this.overlay.hidden = false;
    this.lockScroll();

    // Search always starts closed, however the drawer was last left.
    if (this.searchPanel) {
      this.searchOpen = false;
      this.searchPanel.hidden = true;
      this.searchPanel.style.height = '';
      if (this.searchToggle) this.searchToggle.setAttribute('aria-expanded', 'false');
    }

    // Force a layout read so the browser has a frame boundary to transition
    // FROM, then apply the open state synchronously. Doing this inside
    // requestAnimationFrame is fragile: in a background or throttled frame the
    // callback may never run, leaving the drawer logically open but parked
    // off-screen with no way back.
    void this.panel.offsetWidth;
    this.root.classList.add('is-open');

    var closeBtn = this.panel.querySelector('.onyx-drawer__close');
    if (closeBtn) closeBtn.focus({ preventScroll: true });

    this.syncTriggerState(true);
  };

  OnyxDrawer.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.root.classList.remove('is-open');
    this.syncTriggerState(false);

    var self = this;
    var done = function () {
      self.panel.hidden = true;
      if (self.overlay) self.overlay.hidden = true;
      self.unlockScroll();
      // Reset to the root panel only after the drawer is out of sight, so the
      // level change is never visible on the way out.
      self.resetPanels();
      if (self.lastTrigger && document.body.contains(self.lastTrigger)) {
        self.lastTrigger.focus({ preventScroll: true });
      }
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      done();
      return;
    }

    var fired = false;
    var handler = function () {
      if (fired) return;
      fired = true;
      self.panel.removeEventListener('transitionend', handler);
      done();
    };
    this.panel.addEventListener('transitionend', handler);
    // Timeout is the guarantee, not the fallback: transitionend does not fire
    // in a throttled frame, and the drawer must never be left half-closed.
    setTimeout(handler, 320);
  };

  /* ---------- Keyboard ---------- */

  OnyxDrawer.prototype.focusables = function () {
    var self = this;
    return Array.prototype.filter.call(
      this.panel.querySelectorAll(FOCUSABLE),
      function (el) {
        // Skip anything inside a panel that is not the current one — those are
        // still painted (they slide, they are not display:none) so a plain
        // offsetParent test would let Tab walk into them.
        var owner = el.closest('[data-onyx-panel]');
        if (owner && owner !== self.current) return false;
        return el.offsetParent !== null || el === document.activeElement;
      }
    );
  };

  OnyxDrawer.prototype.onKeydown = function (event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      // Escape dismisses the search panel first, then the drawer.
      if (this.searchOpen) {
        this.closeSearch(true);
      } else {
        this.close();
      }
      return;
    }

    // Left arrow / Backspace outside a text field walks back a level.
    if ((event.key === 'ArrowLeft' || event.key === 'Backspace') &&
        this.current !== this.rootPanel &&
        !/^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement.tagName)) {
      event.preventDefault();
      this.goBack();
      return;
    }

    if (event.key !== 'Tab') return;

    var nodes = this.focusables();
    if (!nodes.length) return;

    var first = nodes[0];
    var last = nodes[nodes.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ---------- Scroll lock ----------
     The body's inline styles are captured before they are touched and restored
     verbatim on close. Blanking them instead would silently destroy inline
     styles set by the theme or another app. */
  OnyxDrawer.prototype.lockScroll = function () {
    var s = document.body.style;
    this.prevBodyStyle = {
      position: s.position,
      top: s.top,
      left: s.left,
      right: s.right,
      width: s.width,
      overflow: s.overflow
    };

    this.scrollY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.classList.add('onyx-drawer-locked');
    // Fixing the body at its current offset is the one approach that holds on
    // both iOS Safari and Android Chrome; overflow:hidden alone does not.
    s.position = 'fixed';
    s.top = -this.scrollY + 'px';
    s.left = '0';
    s.right = '0';
    s.width = '100%';
  };

  OnyxDrawer.prototype.unlockScroll = function () {
    var s = document.body.style;
    var prev = this.prevBodyStyle;

    if (prev) {
      s.position = prev.position;
      s.top = prev.top;
      s.left = prev.left;
      s.right = prev.right;
      s.width = prev.width;
      s.overflow = prev.overflow;
      this.prevBodyStyle = null;
    }

    document.documentElement.classList.remove('onyx-drawer-locked');
    window.scrollTo(0, this.scrollY);
  };

  /* ---------- Boot ---------- */

  function init() {
    var roots = document.querySelectorAll('[data-onyx-drawer-root]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__onyxDrawer) return;
      root.__onyxDrawer = new OnyxDrawer(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
